import { featureFlipping, Locale, Scope } from '@headless-workspace/config';
import {
    PreviewOptions,
    Repository,
    Result,
    ResultEmpty,
    ResultFailureFromApiError,
    ResultSuccess,
} from '@headless-workspace/core-domain';
import {
    AuthorizationStrategy,
    memoryCacheAuthStrategy,
    memoryCacheQueryParamStrategy,
    memoryCacheRefreshTokenStrategy,
    QueryParamStrategy,
    RefreshTokenStrategy,
} from '@headless-workspace/data';
import { ArrayHelpers, DecoderHelpers } from '@headless-workspace/utils';
import { FlagDTO, MediaDTO } from '../../../client';
import {
    BreadcrumbDTO,
    LoyaltyConfigsValue,
    mapProductDetail,
    mapProductStockInformation,
    mapTileProduct,
    ProductDetailBannerDTO,
    ProductDetailValue,
    ProductDetailVariantDTO,
    ProductDetailWithUnknownDTO,
    ProductStockInformationValue,
    ProductStockInformationWithUnknownDTO,
    TileProductDTO,
    TileProductsContainerDTO,
    TileProductValue,
    VariantWithStockInformationDTO,
    VipBrandsValue,
} from '../../../server';

const MAX_QUERY_PRODUCT_IDS = 24;

type GetProductsParams = PreviewOptions & {
    locale: Locale;
    ids: string[];
    vipBrands: VipBrandsValue;
    originalPriceText?: string;
};

type GetProductByIdParams = PreviewOptions & {
    locale: Locale;
    id: string;
    vipBrands: VipBrandsValue;
    needScope?: boolean;
    loyaltyConfig?: LoyaltyConfigsValue;
    variantIds: string[];
};

export interface ProductsRepositorySpec {
    getProducts(params: GetProductsParams): Promise<Result<TileProductValue[]>>;

    getProductById(params: GetProductByIdParams): Promise<Result<ProductDetailValue>>;

    getProductStockInformation(locale: Locale, productId: string): Promise<Result<ProductStockInformationValue>>;
}

const PRODUCTS_SEPARATOR = ';';

export class ProductsRepository extends Repository implements ProductsRepositorySpec {
    readonly path = '/catalog/v1';

    constructor(
        serverAuthStrategy: Partial<AuthorizationStrategy> = memoryCacheAuthStrategy,
        serverRefreshTokenStrategy: Partial<RefreshTokenStrategy> = memoryCacheRefreshTokenStrategy,
        serverQueryParamStrategy: Partial<QueryParamStrategy> = memoryCacheQueryParamStrategy,
    ) {
        super(serverAuthStrategy, serverRefreshTokenStrategy, serverQueryParamStrategy);
    }

    async getProducts({
        locale,
        ids,
        vipBrands,
        originalPriceText,
        isPreviewable = false,
    }: GetProductsParams): Promise<Result<TileProductValue[]>> {
        try {
            const idsChunks = ArrayHelpers.chunkArray(ids, MAX_QUERY_PRODUCT_IDS);

            const responsesChunks = await Promise.all(
                idsChunks.map((idsChunk) => {
                    return this.datasource.getResource<TileProductsContainerDTO>(`${this.path}/products`, {
                        ids: idsChunk.join(PRODUCTS_SEPARATOR),
                        locale,
                        isPreviewable,
                    });
                }),
            );

            const response = responsesChunks.map((chunk) => TileProductsContainerDTO.parse(chunk));
            let products = response.reduce<TileProductDTO[]>((acc, current) => {
                const products = DecoderHelpers.safeParseArray(current.products, TileProductDTO);
                return [...acc, ...products];
            }, []);

            if (featureFlipping.isReorderProductsEnabled) {
                const productsMap = new Map(products.map((product) => [product.id, product]));
                products = ids
                    .map((id) => productsMap.get(id))
                    .filter((product): product is TileProductDTO => product !== undefined);
            }

            if (products.length === 0) {
                return ResultEmpty();
            }

            return ResultSuccess(products.map((product) => mapTileProduct(product, vipBrands, originalPriceText)));
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError, { locale });
        }
    }

    async getProductById({
        locale,
        id,
        vipBrands,
        needScope = false,
        isPreviewable = false,
        loyaltyConfig,
        variantIds,
    }: GetProductByIdParams): Promise<Result<ProductDetailValue>> {
        try {
            const response = await this.datasource.getResource(`${this.path}/products/${id}`, {
                scope: needScope ? Scope.ProductDetailPage : undefined,
                locale,
                isPreviewable,
                selectedVariants: variantIds?.join(PRODUCTS_SEPARATOR),
            });

            const productDetailContainer = ProductDetailWithUnknownDTO.parse(response);
            const variants = DecoderHelpers.safeParseArray(
                productDetailContainer.product.variants,
                ProductDetailVariantDTO,
            );
            const banners = DecoderHelpers.safeParseArray(
                productDetailContainer.product.banners,
                ProductDetailBannerDTO,
            );
            const medias = DecoderHelpers.safeParseArray(productDetailContainer.product.medias, MediaDTO);
            const flags = DecoderHelpers.safeParseArray(productDetailContainer.product.flags, FlagDTO);
            const breadcrumbs = DecoderHelpers.safeParseArray(
                productDetailContainer.product.breadcrumbs,
                BreadcrumbDTO,
            );

            return ResultSuccess(
                mapProductDetail(
                    {
                        ...productDetailContainer.product,
                        banners,
                        variants,
                        medias,
                        flags,
                        breadcrumbs,
                    },
                    vipBrands,
                    locale,
                    loyaltyConfig,
                ),
            );
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError, { locale });
        }
    }

    async getProductStockInformation(locale: Locale, productId: string): Promise<Result<ProductStockInformationValue>> {
        try {
            const response = await this.datasource.getResource(`${this.path}/products/${productId}`, {
                locale,
            });
            const productStockInformationResult = ProductStockInformationWithUnknownDTO.parse(response);

            // FIXME: Handle PRODUCT_SET case => productStockInformationResult.product.products[n].variants
            const variants = DecoderHelpers.safeParseArray(
                productStockInformationResult.product.variants,
                VariantWithStockInformationDTO,
            );

            return ResultSuccess(mapProductStockInformation(variants, productId));
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError, { locale });
        }
    }
}
